// client/src/components/HomeLanding.js
// The homepage when no search or filter is active. Everything here comes from
// live store data (categories, products), and a section with nothing
// to show is left out, so a new or small catalogue never looks empty or repetitive.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Box, Button, Container, IconButton, Skeleton, Stack, Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Banner, { DARK_SECTION_COLOR } from './Banner';
import ProductCard from './ProductCard';
import { useCategories } from '../context/CategoryContext';
import { getCategoryIcon } from '../constants/categoryIcons';
import { getImageUrl } from '../utils/media';

const RAIL_CARD_WIDTH = 248;
const RAIL_GAP = 16;
const MAX_RAIL_PRODUCTS = 12;

const categoryPath = (category, subcategory) => {
  const params = new URLSearchParams({ category });
  if (subcategory) params.set('subcategory', subcategory);
  return `/?${params.toString()}`;
};

// Fades a section up the first time it scrolls into view.
const Reveal = ({ children }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element || !('IntersectionObserver' in window)) {
      setVisible(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: '0px 0px -8% 0px' });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(28px)',
        transition: 'opacity 0.7s ease, transform 0.7s cubic-bezier(0.2, 0.7, 0.2, 1)',
        '@media (prefers-reduced-motion: reduce)': { opacity: 1, transform: 'none', transition: 'none' },
      }}
    >
      {children}
    </Box>
  );
};

const SectionHeader = ({ eyebrow, title, linkTo, linkLabel, children }) => (
  <Stack direction="row" alignItems="flex-end" justifyContent="space-between" spacing={2} sx={{ mb: 3 }}>
    <Box sx={{ minWidth: 0 }}>
      {eyebrow && (
        <Typography variant="overline" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.14em', display: 'block', lineHeight: 1.6 }}>
          {eyebrow}
        </Typography>
      )}
      <Typography component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.25rem' }, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15 }}>
        {title}
      </Typography>
    </Box>
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
      {linkTo && (
        <Button component={Link} to={linkTo} endIcon={<ArrowForwardIcon />} sx={{ color: 'primary.main', whiteSpace: 'nowrap' }}>
          {linkLabel}
        </Button>
      )}
      {children}
    </Stack>
  </Stack>
);

const ShopByCategory = () => {
  const { categories, loaded } = useCategories();
  const shown = categories.filter((category) => category.name !== 'Other');

  if (!loaded || shown.length === 0) return null;

  return (
    <Container maxWidth="xl" component="section" sx={{ pt: { xs: 3, md: 4 }, pb: { xs: 6, md: 9 } }}>
      <SectionHeader eyebrow="Find your hobby" title="Shop by category" />
      <Box
        sx={{
          display: 'grid',
          gap: 1.5,
          // Phones swipe through two rows; larger screens show every category at once.
          gridAutoFlow: { xs: 'column', sm: 'row' },
          gridTemplateRows: { xs: 'repeat(2, auto)', sm: 'none' },
          gridAutoColumns: { xs: '138px', sm: 'auto' },
          gridTemplateColumns: { sm: 'repeat(auto-fill, minmax(160px, 1fr))', lg: 'repeat(7, 1fr)' },
          overflowX: { xs: 'auto', sm: 'visible' },
          mx: { xs: -2, sm: 0 },
          px: { xs: 2, sm: 0 },
          pb: { xs: 1, sm: 0 },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {shown.map((category) => {
          const Icon = getCategoryIcon(category.icon_key);
          return (
            <Box
              key={category.id}
              component={Link}
              to={categoryPath(category.name)}
              sx={{
                display: 'flex', flexDirection: 'column', gap: 1.5, p: category.image_url ? 0 : 2, minHeight: 112, overflow: 'hidden',
                color: 'text.primary', textDecoration: 'none',
                bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 3,
                transition: 'transform 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease',
                '& .category-icon': { transition: 'background-color 0.25s ease, color 0.25s ease' },
                '&:hover, &:focus-visible': {
                  transform: 'translateY(-4px)',
                  borderColor: 'primary.main',
                  boxShadow: (theme) => `0 12px 28px ${alpha(theme.palette.primary.main, 0.18)}`,
                },
                '&:hover .category-icon, &:focus-visible .category-icon': {
                  bgcolor: 'primary.main', color: 'primary.contrastText',
                },
                '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
              }}
            >
              {category.image_url && (
                <Box component="img" src={getImageUrl(category.image_url)} alt="" loading="lazy" sx={{ width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', display: 'block' }} />
              )}
              <Box
                className="category-icon"
                sx={{
                  display: category.image_url ? 'none' : 'grid',
                  width: 40, height: 40, placeItems: 'center', borderRadius: 2,
                  color: 'primary.main', bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
                }}
              >
                <Icon fontSize="small" />
              </Box>
              <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.3, ...(category.image_url ? { px: 2, pb: 2 } : {}) }}>{category.name}</Typography>
            </Box>
          );
        })}
      </Box>
    </Container>
  );
};

const ProductRail = ({ eyebrow, title, products, linkTo, linkLabel, loading }) => {
  const scrollerRef = useRef(null);
  const [canScroll, setCanScroll] = useState({ back: false, forward: false });

  const updateArrows = useCallback(() => {
    const element = scrollerRef.current;
    if (!element) return;
    setCanScroll({
      back: element.scrollLeft > 4,
      forward: element.scrollLeft + element.clientWidth < element.scrollWidth - 4,
    });
  }, []);

  useEffect(() => {
    updateArrows();
    window.addEventListener('resize', updateArrows);
    return () => window.removeEventListener('resize', updateArrows);
  }, [updateArrows, products, loading]);

  const scrollBy = (direction) => {
    const element = scrollerRef.current;
    if (!element) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    element.scrollBy({ left: direction * element.clientWidth * 0.85, behavior: reduceMotion ? 'auto' : 'smooth' });
  };

  if (!loading && products.length === 0) return null;

  return (
    <Container maxWidth="xl" component="section" sx={{ pb: { xs: 6, md: 9 } }}>
      <SectionHeader eyebrow={eyebrow} title={title} linkTo={linkTo} linkLabel={linkLabel}>
        {(canScroll.back || canScroll.forward) && (
          <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', md: 'flex' } }}>
            <IconButton aria-label={`Scroll ${title} back`} disabled={!canScroll.back} onClick={() => scrollBy(-1)} sx={{ border: 1, borderColor: 'divider' }}>
              <ChevronLeftIcon />
            </IconButton>
            <IconButton aria-label={`Scroll ${title} forward`} disabled={!canScroll.forward} onClick={() => scrollBy(1)} sx={{ border: 1, borderColor: 'divider' }}>
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        )}
      </SectionHeader>
      <Box
        ref={scrollerRef}
        onScroll={updateArrows}
        sx={{
          display: 'flex', gap: `${RAIL_GAP}px`, overflowX: 'auto', scrollSnapType: 'x proximity',
          mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 }, pb: 1,
          scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {loading
          ? Array.from({ length: 6 }, (_, index) => (
            <Skeleton key={index} variant="rounded" width={RAIL_CARD_WIDTH} height={380} sx={{ flexShrink: 0, borderRadius: 4 }} />
          ))
          : products.map((product) => (
            <ProductCard key={product.id} product={product} width={RAIL_CARD_WIDTH} sx={{ scrollSnapAlign: 'start' }} />
          ))}
      </Box>
    </Container>
  );
};

const INTERESTS = [
  { category: 'RC Planes', title: 'Take to the skies', text: 'Trainers, aerobatic models, warbirds, gliders and jets.' },
  { category: 'RC Helicopters', title: 'Master the hover', text: 'Beginner, collective pitch and scale helicopters.' },
  { category: 'Model Kits', title: 'Build something', text: 'Aircraft, military, car and ship kits for the workbench.' },
];

const InterestPanels = () => {
  const { categories } = useCategories();
  const panels = INTERESTS
    .map((interest) => ({ ...interest, match: categories.find((category) => category.name === interest.category) }))
    .filter((interest) => interest.match);

  if (panels.length === 0) return null;

  return (
    <Container maxWidth="xl" component="section" sx={{ pb: { xs: 6, md: 9 } }}>
      <Box
        sx={{
          display: 'grid', gap: 2,
          gridTemplateColumns: { xs: '1fr', md: panels.length === 3 ? '1.4fr 1fr 1fr' : `repeat(${panels.length}, 1fr)` },
        }}
      >
        {panels.map((panel, index) => {
          const Icon = getCategoryIcon(panel.match.icon_key);
          const glow = index % 2 === 0 ? 'primary' : 'secondary';
          return (
            <Box
              key={panel.category}
              component={Link}
              to={categoryPath(panel.category)}
              sx={{
                position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                minHeight: { xs: 220, md: 320 }, p: { xs: 3, md: 4 }, borderRadius: 4,
                color: '#fff', textDecoration: 'none', bgcolor: DARK_SECTION_COLOR,
                backgroundImage: (theme) => `radial-gradient(520px 320px at 85% 10%, ${alpha(theme.palette[glow].main, 0.7)}, transparent 70%)`,
                '& .panel-icon': { transition: 'transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1)' },
                '& .panel-arrow': { transition: 'transform 0.25s ease' },
                '&:hover .panel-icon': { transform: 'translate(-6px, -6px) rotate(-4deg) scale(1.06)' },
                '&:hover .panel-arrow': { transform: 'translateX(5px)' },
                '@media (prefers-reduced-motion: reduce)': {
                  '& .panel-icon, & .panel-arrow': { transition: 'none' },
                  '&:hover .panel-icon, &:hover .panel-arrow': { transform: 'none' },
                },
              }}
            >
              <Icon
                className="panel-icon"
                aria-hidden
                sx={{ position: 'absolute', top: { xs: 16, md: 24 }, right: { xs: 16, md: 24 }, fontSize: { xs: 88, md: 128 }, color: 'rgba(255,255,255,0.88)' }}
              />
              <Typography component="h3" sx={{ fontSize: { xs: '1.5rem', md: '1.85rem' }, fontWeight: 800, letterSpacing: '-0.02em', mb: 0.5, maxWidth: '75%' }}>
                {panel.title}
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.78)', mb: 2, maxWidth: 360 }}>{panel.text}</Typography>
              <Stack direction="row" alignItems="center" spacing={0.75} sx={{ fontWeight: 700 }}>
                <Typography variant="body2" fontWeight={700}>Shop {panel.category}</Typography>
                <ArrowForwardIcon className="panel-arrow" fontSize="small" />
              </Stack>
            </Box>
          );
        })}
      </Box>
    </Container>
  );
};

// Shown only while the store still has a Trainers subcategory to send beginners to.
const StarterCallout = () => {
  const { categories } = useCategories();
  const planes = categories.find((category) => category.name === 'RC Planes');
  if (!planes?.children.some((sub) => sub.name === 'Trainers')) return null;

  return (
    <Container maxWidth="xl" component="section" sx={{ pb: { xs: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' },
          justifyContent: 'space-between', gap: 3, p: { xs: 3, md: 6 }, borderRadius: 4,
          color: 'primary.contrastText',
          background: (theme) => `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
        }}
      >
        <Box sx={{ maxWidth: 640 }}>
          <Typography component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.25rem' }, fontWeight: 800, letterSpacing: '-0.025em', lineHeight: 1.15, mb: 1 }}>
            New to RC flying?
          </Typography>
          <Typography sx={{ opacity: 0.9 }}>
            Trainer planes are built for first-time pilots. Start with one, then add spare batteries so you can stay in the air for longer.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ flexShrink: 0, width: { xs: '100%', sm: 'auto' } }}>
        <Button
          component={Link}
          to="/beginner-guide"
          size="large"
          variant="outlined"
          sx={{ color: 'primary.contrastText', borderColor: 'primary.contrastText', '&:hover': { borderColor: 'primary.contrastText' } }}
        >
          Help me choose
        </Button>
        <Button
          component={Link}
          to={categoryPath('RC Planes', 'Trainers')}
          size="large"
          endIcon={<ArrowForwardIcon />}
          sx={{
            flexShrink: 0, bgcolor: 'primary.contrastText', color: 'primary.dark',
            '&:hover': { bgcolor: 'primary.contrastText', opacity: 0.9 },
          }}
        >
          Shop trainer planes
        </Button>
        </Stack>
      </Box>
    </Container>
  );
};

const HomeLanding = ({ products, recentlyViewed, loading, error }) => {
  // The API returns newest first. Each section takes different products, so a
  // small catalogue doesn't show the same items over and over.
  const newArrivals = products.slice(0, MAX_RAIL_PRODUCTS);
  const specials = products
    .filter((product) => Number(product.compare_at_price) > Number(product.price))
    .slice(0, MAX_RAIL_PRODUCTS);
  const specialIds = new Set(specials.map((product) => product.id));
  const moreToExplore = products
    .slice(MAX_RAIL_PRODUCTS)
    .filter((product) => !specialIds.has(product.id))
    .slice(0, MAX_RAIL_PRODUCTS);

  return (
    <Box>
      <Banner />
      <ShopByCategory />
      {error && (
        <Container maxWidth="xl" sx={{ pb: 6 }}><Alert severity="error">{error}</Alert></Container>
      )}
      {!error && (
        <Reveal>
          <ProductRail eyebrow="Just in" title="New arrivals" products={newArrivals} loading={loading} linkTo="/?sort=newest" linkLabel="View all" />
        </Reveal>
      )}
      <Reveal><InterestPanels /></Reveal>
      <Reveal><ProductRail eyebrow="Save more" title="On special" products={specials} linkTo="/?sale=true" linkLabel="All specials" /></Reveal>
      <Reveal><ProductRail eyebrow="Keep browsing" title="More to explore" products={moreToExplore} /></Reveal>
      <Reveal><ProductRail eyebrow="Pick up where you left off" title="Recently viewed" products={recentlyViewed} /></Reveal>
      <Reveal><StarterCallout /></Reveal>
    </Box>
  );
};

export default HomeLanding;
